/**
 * Spec 76 Phase E — useCloseSummary
 *
 * Watches the URL panel state for an open→closed transition. When the closing
 * panel is an editor type with a `generateCloseSummary` callback, the hook:
 *
 *   1. Calls `generateCloseSummary(id)` to get the entity-specific summary text.
 *   2. POSTs to `/conversations/:childConvId/close-panel` — the server writes
 *      the system-role `{ contextPanelClosed: { type, id, summary } }` card to
 *      the PARENT conversation and stamps `close_summary_posted_at` on the
 *      CHILD. The {type, id} echoed in the card come from the child row's
 *      `subjectType`/`subjectId` (NOT from this client) — preventing forgery.
 *   3. Invalidates the parent message-list query so the new card appears.
 *
 * Viewer-mode panels and editor panels with no `generateCloseSummary` defined
 * skip the post entirely — the hook is inert.
 *
 * The child conv id is read from the TanStack Query cache populated by
 * `useThreadSwap` (keyed by `threadSwapQueryKey`). If the
 * cache is empty (panel was never editor-resolved), the post is skipped.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { listConversationMessagesQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import { conversationsApi } from '../../services/api/conversations.api.js';
import { ApiError } from '../../services/api/errors.js';
import { getPanelType } from './registry.js';
import { threadSwapQueryKey } from './use-thread-swap.js';
import { useContextPanel, type PanelPointer } from './url-state.js';

const SUMMARY_MAX_CHARS = 2000;

export interface UseCloseSummaryOptions {
  /** Parent conversation id (the one that should receive the close-summary card). */
  parentConversationId: string;
}

export function useCloseSummary({ parentConversationId }: UseCloseSummaryOptions): void {
  const { panel } = useContextPanel();
  const queryClient = useQueryClient();
  const prevPanelRef = useRef<PanelPointer | null>(null);

  useEffect(() => {
    const prev = prevPanelRef.current;
    prevPanelRef.current = panel;

    // Only fire on transition from open → closed. Same-pointer updates
    // (Strict-Mode double effects, query refresh) won't trigger.
    if (prev === null || panel !== null) return;

    const definition = getPanelType(prev.type);
    if (definition?.mode !== 'editor' || !definition.generateCloseSummary) return;

    // Read the child conv id from the thread-swap query cache. If absent
    // (the user closed before find-or-create resolved), skip.
    const cached = queryClient.getQueryData<{ id: string }>(
      threadSwapQueryKey(parentConversationId, prev.type, prev.id),
    );
    const childConvId = cached?.id;
    if (!childConvId) return;

    // Fire-and-forget — the URL has already changed by the time this hook
    // sees the transition. The worst case for a failed post is "no echo
    // card", recoverable by re-opening the panel and triggering re-arm.
    void (async () => {
      try {
        const summary = await definition.generateCloseSummary!(prev.id);
        if (!summary) return;

        // Truncate defensively — server enforces 2000 chars too.
        const trimmed = summary.length > SUMMARY_MAX_CHARS ? summary.slice(0, SUMMARY_MAX_CHARS) : summary;

        await conversationsApi.closeContextPanel({
          path: { id: childConvId },
          body: { summary: trimmed },
        });

        // Invalidate parent message list so the new system card renders.
        // Key must match ConversationMessageList's useQuery key.
        await queryClient.invalidateQueries({
          queryKey: listConversationMessagesQueryKey({ path: { id: parentConversationId } }),
        });
      } catch (err) {
        // Distinguish HTTP errors from unexpected errors so devs notice
        // policy regression in testing instead of silently swallowing 4xx.
        // 409 is expected (idempotency: card already posted, no re-arm yet);
        // log at debug level, not error.
        const status = err instanceof ApiError ? err.status : undefined;
        if (status === 409) {
          // eslint-disable-next-line no-console
          console.debug('[ContextPanel] close-summary skipped — already posted (409)');
        } else if (status === 401 || status === 403) {
          // Policy denial — surface so it's visible in the console; UI
          // intentionally has no retry affordance (the user has already
          // navigated away by closing the panel).
          // eslint-disable-next-line no-console
          console.error(`[ContextPanel] close-summary denied (${status})`, err);
        } else {
          // eslint-disable-next-line no-console
          console.warn('[ContextPanel] close-summary post failed', err);
        }
      }
    })();
  }, [panel, parentConversationId, queryClient]);
}
